import _ from 'lodash';
import React, {Component} from 'react';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../../../../libs/shared/core/src/models/api/teamModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ResourceMap} from '../../../../../../../libs/shared/core/src/store/commonAppTypes';
import {DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {
  AsyncSearchDropdown,
  AsyncSearchDropdownDataFetcher,
} from '../../../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {findInboxNameDuplicates} from '../../../../../../../srcCommon/inboxes/viewer/conversation/moveDropdownHelpers';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {teammateCanAccessInbox} from '../../../../../../helpers/modelHelpers';
import {canMoveToInbox} from '../../../../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {TrackerInfo} from '../../../../../../store/data/conversations/conversationsSelectors';
import {
  MoveToInboxSourceDropdown,
  MoveToInboxSourceTypesEnum,
} from '../../../../../../store/data/conversations/moveConversationsActions';
import {BulkActionConfirmationDialog} from '../../actions/bulk/bulkActionConfirmationDialog';
import {SafetyNetActionsEnum} from '../../safetyNets/safetyNetsContext';
import {SafetyNetsCoordinator} from '../../safetyNets/safetyNetsCoordinator';
import {MoveAccessDialog} from './moveAccessDialogContainer';
import {MoveDropdownItem} from './moveDropdownItem';

/*
 * Types.
 */

export type MoveHandler = (
  inbox: InboxModel,
  source: MoveToInboxSourceDropdown,
  shouldRetainAccess?: boolean,
) => void;

/*
 * Props.
 */

interface MoveDropdownProps extends DropdownProps {
  user: TeammateModel;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  conversations: ReadonlyArray<ConversationBaseModel>;
  conversationInboxIds: ReadonlySet<number>;
  onMove: MoveHandler;
  onBulkMove: (model: InboxModel) => void;
  isOpenedByShortcut: boolean;
  teamsByNamespace: ResourceMap<TeamModel>;
  fetchInboxesAsync: AsyncSearchDropdownDataFetcher<InboxModel, any>;
  isBulkActionRequired: boolean;
  trackers: ReadonlyArray<TrackerInfo>;
}

/*
 * Component.
 */

interface MoveDropdownState {
  unreachableInbox?: InboxModel;
  bulkMoveInbox?: InboxModel;
  query: string;
}

export class MoveDropdown extends Component<MoveDropdownProps, MoveDropdownState> {
  constructor(props: MoveDropdownProps) {
    super(props);
    this.state = {query: ''};
  }

  /*
   * Event handlers.
   */

  private readonly onInboxSelect: (model: InboxModel) => void = (inbox) => {
    const {user, onMove, isBulkActionRequired, isOpenedByShortcut} = this.props;

    if (isBulkActionRequired) {
      // If we need to do a bulk action, show a conversation first.
      this.setState({bulkMoveInbox: inbox});
      return;
    }

    // If we don't have access, display a dialog.
    const userHasAccess = teammateCanAccessInbox(user, inbox);
    const isDestinationUnreachable =
      !teammateCanAccessInbox(user, inbox) ||
      this.props.trackers.some(
        (t) => t.teammate.id !== user.id && !teammateCanAccessInbox(t.teammate, inbox),
      );

    if (!userHasAccess || isDestinationUnreachable) {
      this.setState({unreachableInbox: inbox});
      return;
    }

    onMove(inbox, {
      type: MoveToInboxSourceTypesEnum.DROPDOWN,
      isOpenedByShortcut,
      queryText: this.state.query,
      isInboxUnreachable: false,
    });

    // Tracking.
    trackInterfaceEvent(InterfaceEventsEnum.MOVE_MENU, {
      step: 'move_to_inbox',
      destination_inbox: inbox.id,
    });
    this.props.onRequestClose?.();
  };

  private readonly moveToInbox = (inbox: InboxModel, shouldRetainAccess?: boolean) => {
    const {onMove, isOpenedByShortcut} = this.props;

    this.hideUnreachableInboxAlert();
    onMove(
      inbox,
      {
        type: MoveToInboxSourceTypesEnum.DROPDOWN,
        isOpenedByShortcut,
        queryText: this.state.query,
        isInboxUnreachable: true,
      },
      shouldRetainAccess,
    );
    this.props.onRequestClose?.();
  };

  private readonly bulkMoveToInbox = (inbox: InboxModel) => {
    const {onBulkMove} = this.props;

    this.hideBulkActionConfirmation();
    onBulkMove(inbox);
    this.props.onRequestClose?.();
  };

  private readonly hideUnreachableInboxAlert = () => {
    this.setState({unreachableInbox: undefined});
  };

  private readonly hideBulkActionConfirmation = () => {
    this.setState({bulkMoveInbox: undefined});
  };

  /*
   * Render.
   */

  render() {
    return (
      <>
        {this.renderSearchDropdown()}
        {this.maybeRenderInboxUnreachableDialog()}
        {this.maybeRenderBulkMoveConfirmation()}
      </>
    );
  }

  private renderSearchDropdown() {
    return (
      <AsyncSearchDropdown
        {...this.props}
        width={260}
        headerTitle={<Trans id="dropdown-move-title">Move to</Trans>}
        query={this.state.query}
        onQueryChange={(newQuery) => this.setState({query: newQuery})}
        onRequestData={this.props.fetchInboxesAsync}
        renderRow={this.renderRow}
        transformDataForRender={this.transformDropdownDataForRender}
      />
    );
  }
  private readonly transformDropdownDataForRender = (inboxes: ReadonlyArray<InboxModel>) => {
    if (this.state.query) {
      return inboxes;
    }
    return _(this.props.conversationsInboxes)
      .concat(inboxes)
      .uniqBy((i) => i.id)
      .value();
  };

  private readonly renderRow = (inbox: InboxModel, index: number, items: ReadonlyArray<InboxModel>) => {
    const {teamsByNamespace, conversationInboxIds, conversations} = this.props;
    const inboxNameDuplicates = findInboxNameDuplicates(items);

    return (
      <SafetyNetsCoordinator
        conversations={this.props.conversations}
        safetyNetActionType={SafetyNetActionsEnum.MOVE}
        onActionValidated={() => this.onInboxSelect(inbox)}
      >
        {(onSelect) => (
          <MoveDropdownItem
            key={inbox.id}
            index={index}
            inbox={inbox}
            onSelect={(e) => {
              e.preventDefault();
              onSelect();
            }}
            isChecked={conversationInboxIds.has(inbox.id)}
            team={teamsByNamespace[inbox.namespace]}
            shouldShowDeduplication={inboxNameDuplicates.has(inbox.name)}
            shouldShowTeammateCount={false}
            isBold={false}
            isDisabled={!canMoveToInbox(inbox, conversations)}
          />
        )}
      </SafetyNetsCoordinator>
    );
  };

  private maybeRenderInboxUnreachableDialog() {
    const {conversations} = this.props;
    const {unreachableInbox} = this.state;
    if (!unreachableInbox) {
      return null;
    }

    return (
      <MoveAccessDialog
        destinationInbox={unreachableInbox}
        conversationsCount={conversations.length}
        onKeepAccessClick={() => this.moveToInbox(unreachableInbox, true)}
        onLoseAccessClick={() => this.moveToInbox(unreachableInbox, false)}
        onRequestClose={() => this.hideUnreachableInboxAlert()}
        isUserOnlyFollower={Boolean(
          this.props.trackers.length === 1 && this.props.trackers[0].teammate.id === this.props.user.id,
        )}
      />
    );
  }

  private maybeRenderBulkMoveConfirmation() {
    const {bulkMoveInbox} = this.state;
    if (!bulkMoveInbox) {
      return null;
    }

    return (
      <BulkActionConfirmationDialog
        onConfirmClick={() => this.bulkMoveToInbox(bulkMoveInbox)}
        onRequestClose={this.hideBulkActionConfirmation}
        actionType={BulkActionTypesEnum.MOVE}
      />
    );
  }
}
